import { Divider } from 'antd';
import { ProductInfoImage } from '../ProductInfo/Image';
import { cx } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { ProductInfoTitle } from '../ProductInfo/Title';
import { formatNumber } from 'utils/numberFormat';
import { CopyText } from '../CopyText';
import { useOrderProductShowPrice } from 'hooks/business/chat/useOrderProduct';
import { usePreOrder } from 'hooks/business/chat/usePreOrder';
import { isNil } from 'lodash-es';
import { OrderComment } from './OrderComment';

interface OrderProductProps {
  className?: string;
  simple?: boolean;

  platform: General.Platform;
  unit: string;
  itemName: string;
  itemPic: string;
  itemUrl?: string;
  unitPrice?: number;
  sku?: string;
  sellerSku?: string;
  quantity: number;
  promotionType?: Conversation.Trade.Product['promotionType'];
  discountedPrice: number;
  isPreOrder?: boolean;
  digitalInfo?: string;
  warehouse?: string;
  goodsPrepareTime?: number;

  contentExtra?: React.ReactNode;

  /** 是否显示评价 */
  showComment?: boolean;
  itemComments?: Conversation.Trade.Comment[];
}

const UnitPriceContent = (props: {
  unitPrice?: number;
  promotionType?: Conversation.Trade.Product['promotionType'];
  platform: General.Platform;
  unit: string;
  quantity: number;
}) => {
  const { unitPrice, promotionType, platform, unit, quantity } = props;
  const showUnitPrice = useOrderProductShowPrice(platform, promotionType);

  if (showUnitPrice && !isNil(unitPrice) && quantity > 1) {
    return (
      <span className="whitespace-nowrap">
        {unit} {formatNumber(unitPrice, '0,0.00')} x{quantity}
      </span>
    );
  }

  return <span>x{quantity}</span>;
};

const SimpleDetail = (props: { sku?: string; sellerSku?: string; quantity?: number }) => {
  const { sku, sellerSku, quantity } = props;
  const { t } = useTranslate();

  return (
    <div
      className={cx(
        'inline-flex items-center flex-nowrap overflow-hidden min-w-0',
        quantity && quantity === 1 ? 'max-w-[80%]' : 'max-w-[60%]',
      )}
    >
      {sku && (
        <>
          <CopyText text={sku} tooltip={`SKU: ${sku}`} showIcon className="flex-1 min-w-0" />
          <Divider type="vertical" className="!ml-0 !mr-[12px]" />
        </>
      )}
      {sellerSku && (
        <CopyText
          text={sellerSku}
          tooltip={`${t('sider.SKU货号')}: ${sellerSku}`}
          showIcon
          className="flex-1 min-w-0"
        />
      )}
    </div>
  );
};

interface ComplexDetailProps {
  sku?: string;
  sellerSku?: string;
  digitalInfo?: string;
  warehouse?: string;
  goodsPrepareTime?: number;
}
const ComplexDetail = (props: ComplexDetailProps) => {
  const { sku, sellerSku, digitalInfo, warehouse, goodsPrepareTime } = props;
  const { t } = useTranslate();
  return (
    <div className="flex flex-col gap-1">
      {sku && (
        <div>
          SKU:
          <CopyText text={sku} showIcon />
        </div>
      )}
      {sellerSku && (
        <div>
          {t('sider.SKU货号')}:
          <CopyText text={sellerSku} showIcon />
        </div>
      )}
      {digitalInfo && (
        <div>
          {t('数字信息')}:
          <CopyText text={digitalInfo} showIcon />
        </div>
      )}
      {warehouse && (
        <div>
          {t('仓库名称')}:
          <CopyText text={warehouse} showIcon />
        </div>
      )}
      {goodsPrepareTime && goodsPrepareTime > 0 ? (
        <div>
          {t('trade_label_goods_prepare_time')}:
          <CopyText text={t('aide.x天', { x: goodsPrepareTime })} showIcon />
        </div>
      ) : null}
    </div>
  );
};

export const OrderProduct = (props: OrderProductProps) => {
  const {
    className,
    platform,
    unit,
    simple = true,
    itemName,
    itemPic,
    itemUrl,
    unitPrice,
    sku,
    sellerSku,
    quantity,
    promotionType,
    discountedPrice,
    isPreOrder,
    digitalInfo,
    warehouse,
    goodsPrepareTime,
    contentExtra,
    showComment = false,
    itemComments = [],
  } = props;

  const { t } = useTranslate();

  const preOrder = usePreOrder(platform, isPreOrder);

  return (
    <div className={cx('min-h-[48px] flex gap-2 items-start', className)}>
      <ProductInfoImage imgUrl={itemPic} itemName={itemName} platform={platform} />
      <div
        className={cx(
          'grid grid-cols-[1fr_auto] gap-y-1 min-w-0 flex-1',
          simple ? 'gap-x-3' : 'gap-x-6',
        )}
      >
        <div className="flex-1 min-w-0 flex items-center gap-1">
          {preOrder && (
            <span
              className="inline-block text-[#00BF69] bg-[#EBFAF3] text-[12px] leading-[22px] px-1 rounded-sm whitespace-nowrap"
              title={t('trade.预购商品')}
            >
              {t('trade.预购商品')}
            </span>
          )}
          <ProductInfoTitle title={itemName} url={itemUrl} className="min-w-0" />
        </div>
        <span className="text-[#232426] text-xs whitespace-nowrap flex-shrink-0 self-start leading-[22px]">
          {unit} {formatNumber(discountedPrice, '0,0.00')}
        </span>
        <div className="col-span-2 text-xs text-[#84868C] flex items-start gap-4 justify-between">
          {simple ? (
            <SimpleDetail sku={sku} sellerSku={sellerSku} quantity={quantity} />
          ) : (
            <ComplexDetail
              sku={sku}
              sellerSku={sellerSku}
              digitalInfo={digitalInfo}
              warehouse={warehouse}
              goodsPrepareTime={goodsPrepareTime}
            />
          )}
          <div className="flex-shrink-0 flex flex-col flex-start items-end gap-1">
            <div className="flex items-center gap-1">
              {contentExtra}
              <UnitPriceContent
                unitPrice={unitPrice}
                promotionType={promotionType}
                platform={platform}
                unit={unit}
                quantity={quantity}
              />
            </div>
            {!simple && showComment && itemComments?.length > 0 && (
              <OrderComment platform={platform} comments={itemComments} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
